// Saldi per PERSONA invece che per gruppo: quanto devi / ti spetta in totale
// con ciascuno, sommando tutti i gruppi che condividete.
//
// Il nodo è l'identità: lo stesso essere umano è un `member` diverso in ogni
// gruppo. Due casi:
//   • member collegato a un account Gunsi → chiave `u:<userId>`, sicura
//   • ghost (solo un nome) → chiave sul nome normalizzato. Se quel nome
//     coincide con quello di una persona collegata, si unisce a lei: è quasi
//     sempre la stessa persona invitata prima e collegata dopo.
// L'unione per nome resta un'euristica (due "Marco" diversi in gruppi diversi
// finirebbero insieme), ed è per questo che il dettaglio persona mostra
// sempre la ripartizione per gruppo: l'errore si vede e si corregge
// collegando gli account.

import { itemCurrency, usedCurrencies, pairwiseNet } from './balances.js'
import { youOf } from './you.js'
import { convert } from './fx.js'

const EPS = 0.005

function normName(s) {
  return String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
}

// nome normalizzato → userId, solo dove il nome identifica UN solo account
// (nomi ambigui → null: meglio due voci separate che una somma sbagliata)
function linkedNameMap(groups) {
  const byName = new Map()
  for (const g of groups ?? []) {
    for (const m of g.members ?? []) {
      if (!m.userId) continue
      const n = normName(m.name)
      if (!n) continue
      if (!byName.has(n)) byName.set(n, m.userId)
      else if (byName.get(n) !== m.userId) byName.set(n, null)
    }
  }
  return byName
}

function personKey(member, nameMap) {
  if (member.userId) return `u:${member.userId}`
  const n = normName(member.name)
  const linked = n ? nameMap.get(n) : null
  if (linked) return `u:${linked}`
  return n ? `n:${n}` : `m:${member.id}`
}

// → { people: [...], totals: [{ currency, owe, owed }], missingRates }
//
// `target` null = un importo per ogni valuta usata (verità contabile).
// `target` valorizzato = tutto convertito lì al tasso storico di ogni
// movimento (serve `ratesByDate` da getRatesForDates).
export function computePeopleBalances({
  groups, expenses, settlements, youUserId, target = null, ratesByDate = null,
}) {
  const nameMap = linkedNameMap(groups)
  const people = new Map()
  let missingRates = false

  const ensure = (key, member) => {
    let p = people.get(key)
    if (!p) {
      p = { key, name: member.name || '?', userId: member.userId ?? null, groups: new Map(), amounts: new Map() }
      people.set(key, p)
    }
    // Il nome dell'account collegato vince su quello scritto a mano
    if (member.userId && !p.userId) { p.userId = member.userId; p.name = member.name || p.name }
    return p
  }

  for (const g of groups ?? []) {
    const you = youOf(g, youUserId)
    if (!you) continue
    const gExp = (expenses ?? []).filter(e => e.groupId === g.id)
    const gSet = (settlements ?? []).filter(s => s.groupId === g.id)

    // Un passaggio per valuta, oppure uno solo convertito nel target
    const runs = target
      ? [{
          currency: target,
          expenses: gExp,
          settlements: gSet,
          amountOf: (it) => {
            const from = itemCurrency(it, g)
            if (from === target) return it.amount
            return convert(it.amount, from, target, ratesByDate?.[(it.date || '').slice(0, 10)])
          },
        }]
      : usedCurrencies(g, gExp, gSet).map(c => ({
          currency: c,
          expenses: gExp.filter(e => itemCurrency(e, g) === c),
          settlements: gSet.filter(s => itemCurrency(s, g) === c),
          amountOf: (it) => it.amount,
        }))

    // Le persone del gruppo esistono anche se il saldo è a zero: "in pari"
    // è un'informazione, non un'assenza
    for (const m of g.members ?? []) {
      if (m.id === you.id) continue
      const p = ensure(personKey(m, nameMap), m)
      if (!p.groups.has(g.id)) {
        p.groups.set(g.id, { id: g.id, name: g.name, emoji: g.emoji || '👥', amounts: new Map() })
      }
    }

    for (const run of runs) {
      const { net, missing } = pairwiseNet({
        group: g, expenses: run.expenses, settlements: run.settlements,
        youId: you.id, amountOf: run.amountOf,
      })
      if (missing) missingRates = true

      for (const m of g.members ?? []) {
        if (m.id === you.id) continue
        const v = net.get(m.id) ?? 0
        if (Math.abs(v) < EPS) continue
        const p = ensure(personKey(m, nameMap), m)
        p.amounts.set(run.currency, (p.amounts.get(run.currency) ?? 0) + v)
        const gr = p.groups.get(g.id)
        gr.amounts.set(run.currency, (gr.amounts.get(run.currency) ?? 0) + v)
      }
    }
  }

  const toRows = (map) => [...map.entries()]
    .map(([currency, net]) => ({ currency, net: Math.round(net * 100) / 100 }))
    .filter(r => Math.abs(r.net) >= EPS)
    .sort((a, b) => Math.abs(b.net) - Math.abs(a.net))

  const list = [...people.values()].map(p => ({
    key: p.key,
    name: p.name,
    userId: p.userId,
    linked: Boolean(p.userId),
    amounts: toRows(p.amounts),
    groups: [...p.groups.values()]
      .map(g => ({ ...g, amounts: toRows(g.amounts) }))
      .sort((a, b) => {
        const av = Math.max(0, ...a.amounts.map(x => Math.abs(x.net)))
        const bv = Math.max(0, ...b.amounts.map(x => Math.abs(x.net)))
        return bv - av || a.name.localeCompare(b.name)
      }),
  }))

  // Prima chi ha un saldo aperto (dal più grosso), poi chi è in pari
  list.sort((a, b) => {
    const av = Math.max(0, ...a.amounts.map(x => Math.abs(x.net)))
    const bv = Math.max(0, ...b.amounts.map(x => Math.abs(x.net)))
    return bv - av || a.name.localeCompare(b.name)
  })

  // Totali: i crediti e i debiti restano separati (devo 50 a uno e me ne
  // devono 50 non è "in pari": sono due conti aperti diversi)
  const totals = new Map()
  for (const p of list) {
    for (const { currency, net } of p.amounts) {
      const cur = totals.get(currency) ?? { currency, owe: 0, owed: 0 }
      if (net > 0) cur.owed += net
      else cur.owe += -net
      totals.set(currency, cur)
    }
  }

  return {
    people: list,
    totals: [...totals.values()]
      .map(t => ({ ...t, owe: Math.round(t.owe * 100) / 100, owed: Math.round(t.owed * 100) / 100 }))
      .sort((a, b) => (b.owe + b.owed) - (a.owe + a.owed)),
    missingRates,
  }
}

// Iniziali per l'avatar: "Alberto Marson" → "AM", "Hyur" → "HY"
export function initialsOf(name) {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}
